import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface BalanceSheetResponse {
  period: string;
  assets_agorot: number;
  liabilities_agorot: number;
  equity_agorot: number;
  status: string;
  generated_at: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtIls(agorot: number): string { return '₪' + (agorot / 100).toFixed(2); }
export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function closeStatusLabel(s: string): string {
  switch (s) {
    case 'draft': return 'Draft';
    case 'review': return 'Under review';
    case 'approved': return 'Approved';
    case 'filed': return 'Filed';
    default: return 'Unknown';
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div style={{
      background: highlight ? 'rgba(125,211,252,0.08)' : 'rgba(255,255,255,0.04)',
      border: highlight ? '1px solid rgba(125,211,252,0.4)' : '1px solid rgba(255,255,255,0.08)',
      borderRadius: 6, padding: '14px 18px', minWidth: 140,
    }}>
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '16pt', fontWeight: 700, color: 'var(--omega-ink)' }}>{value}</div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceBalanceSheetPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ['finance', 'balance-sheet', period],
    queryFn: () => apiFetch<BalanceSheetResponse>(`/finance/balance-sheet?period=${period}`),
    refetchInterval: 30_000,
  });

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Balance sheet' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Balance sheet — Balance Sheet
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>Period:</label>
          <input
            type="text"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            placeholder="YYYY-MM"
            style={inputStyle}
          />
        </div>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}

      {data && (
        <>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
            <StatCard label="Assets" value={fmtIls(data.assets_agorot)} highlight />
            <StatCard label="Liabilities" value={fmtIls(data.liabilities_agorot)} />
            <StatCard label="Equity" value={fmtIls(data.equity_agorot)} />
          </div>
          <div style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink-subtle)' }}>
            Status: <span style={{ color: 'var(--omega-ink)' }}>{closeStatusLabel(data.status)}</span>
            {'  '}Created: <span style={{ color: 'var(--omega-ink)' }}>{fmtDate(data.generated_at)}</span>
          </div>
        </>
      )}
    </div>
  );
}
